package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.agent.CandidatoFarmacista
import com.heelion.farmafreelance.agent.CriteriFarmacista
import com.heelion.farmafreelance.agent.RicercaFarmacisti
import com.heelion.farmafreelance.models.AnnuncioTipo
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Professione
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Farmy suggerisce i farmacisti per i turni appena pubblicati da una farmacia (punto 2):
 * [turni] = i turni nuovi, [indice] = quello mostrato, [candidati] = i migliori 3 (null = in caricamento).
 * [invitati] = id dei farmacisti già invitati su quel turno, [errore] = ultimo errore da mostrare.
 */
data class SuggerimentoTurni(
    val turni: List<AnnuncioTurno>,
    val indice: Int = 0,
    val candidati: List<CandidatoFarmacista>? = null,
    val invitati: Set<String> = emptySet(),
    val errore: String? = null
) {
    val turno: AnnuncioTurno get() = turni[indice]
}

/** I turni della farmacia che corrispondono a quelli appena pubblicati (date e orari del form). */
fun turniAppenaPubblicati(miei: List<AnnuncioTurno>, dateMillis: List<Long>, oraInizio: String, oraFine: String): List<AnnuncioTurno> {
    val date = dateMillis.map { kotlinx.datetime.Instant.fromEpochMilliseconds(it).toLocalDateTime(TimeZone.UTC).date }.toSet()
    return miei.filter {
        it.id != null && it.tipo == AnnuncioTipo.OFFRO_LAVORO && it.oraInizio == oraInizio && it.oraFine == oraFine &&
            it.dataTurno.toLocalDateTime(TimeZone.UTC).date in date
    }.distinctBy { it.dataTurno }.sortedBy { it.dataTurno }
}

/**
 * I migliori 3 professionisti (farmacisti o infermieri, come chiede il turno) per un turno della
 * farmacia: stessa ricerca di "cerco farmacista…" di Farmy (zona della farmacia, disponibilità
 * pubblicata per quel giorno, tariffa entro il compenso offerto).
 */
suspend fun candidatiPerTurno(viewModel: AuthViewModel, turno: AnnuncioTurno): List<CandidatoFarmacista> {
    val provincia = viewModel.zonaUtente.orEmpty()
    val professione = turno.professioneRichiesta
    val criteri = CriteriFarmacista(
        professione = professione,
        tariffaMax = turno.compenso,
        giorno = turno.dataTurno.toLocalDateTime(TimeZone.UTC).date,
        provincia = provincia.ifBlank { null }
    )
    val farmacisti = if (professione == Professione.INFERMIERE) viewModel.searchInfermieri(provincia = provincia)
        else viewModel.searchFarmacisti(provincia = provincia)
    val annunci = viewModel.getAnnunciControparte(provincia.ifBlank { null }, professione).orEmpty()
    val preferiti = viewModel.currentPreferiti.map { it.targetId }.toSet()
    return RicercaFarmacisti.ordina(farmacisti, annunci, criteri, preferiti).take(3)
}

@Composable
fun SuggerimentoFarmyCard(
    stato: SuggerimentoTurni,
    themeColor: Color,
    onInvita: (CandidatoFarmacista) -> Unit,
    onCambiaTurno: (Int) -> Unit,
    onChiudi: () -> Unit
) {
    val t = stato.turno
    val g = t.dataTurno.toLocalDateTime(TimeZone.UTC).date
    val colore = if (themeColor == Color.White) Color.Gray else themeColor
    Card(
        modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
        colors = CardDefaults.cardColors(containerColor = lerp(Color.White, colore, 0.10f)),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "Farmy: ${chi(t)} adatti", style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold, color = colore, modifier = Modifier.weight(1f)
                )
                IconButton(onClick = onChiudi) { Icon(Icons.Default.Close, "Chiudi") }
            }
            val compenso = t.compenso?.let { c -> ", ${if (c % 1.0 == 0.0) c.toInt().toString() else c.toString().replace('.', ',')} €/h" }.orEmpty()
            Text("Turno del ${g.dayOfMonth}/${g.monthNumber}, ${t.oraInizio}-${t.oraFine}$compenso", style = MaterialTheme.typography.bodyMedium)

            // Più turni pubblicati insieme: si sceglie per quale giorno vedere i suggerimenti.
            if (stato.turni.size > 1) {
                Row(modifier = Modifier.padding(top = 8.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    TextButton(onClick = { onCambiaTurno(stato.indice - 1) }, enabled = stato.indice > 0) { Text("‹ Giorno prima") }
                    TextButton(onClick = { onCambiaTurno(stato.indice + 1) }, enabled = stato.indice < stato.turni.size - 1) { Text("Giorno dopo ›") }
                }
            }
            Spacer(Modifier.height(8.dp))

            val candidati = stato.candidati
            when {
                candidati == null -> Row(verticalAlignment = Alignment.CenterVertically) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                    Spacer(Modifier.width(8.dp))
                    Text(if (t.professioneRichiesta == Professione.INFERMIERE) "Cerco gli infermieri più adatti…" else "Cerco i farmacisti più adatti…")
                }
                candidati.isEmpty() -> Text("Non trovo ${chi(t)} in zona per questo turno. Riceverai le candidature appena arrivano.", color = Color.Gray)
                else -> candidati.forEach { c ->
                    val id = c.farmacista.id
                    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                        Text(RicercaFarmacisti.descrivi(c), style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                        Spacer(Modifier.width(8.dp))
                        if (id != null && id in stato.invitati) {
                            Text("Invitato", color = Color(0xFF2E7D32), style = MaterialTheme.typography.labelLarge)
                        } else {
                            Button(onClick = { onInvita(c) }, enabled = id != null) { Text("Invita") }
                        }
                    }
                }
            }
            stato.errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
        }
    }
}

/** "farmacisti" / "infermieri": chi cerca il turno [t]. */
private fun chi(t: AnnuncioTurno) = if (t.professioneRichiesta == Professione.INFERMIERE) "infermieri" else "farmacisti"
